/* components/ttk/TTKViewBlocks.jsx — переиспользуемые READ-блоки ТТК.
   Принимают данные ТОЛЬКО через пропсы (без импорта моков) — S6 TTKCanvas
   возьмёт их без переписки. Derived-значения приходят посчитанными извне.
   Содержит: KbjuTable, CostSummaryBlock, TTKMetricsBlock, TTKSellingPriceBlock,
   TTKUsedInBlock, IngredientName, RecipeBindingSummary, YieldHint,
   PortionsSelector (=ScaleNormBlock control), TTKSnapshotView.
   ХЕНДОФФ → frontend/src/components/ttk/ (по файлу на компонент при переносе). */

import React from 'react';
import { CostStatusBadge, FoodCostBadge, RecipeBindingBadge, fcTone } from '../ui/Badge.jsx';
import { NumberInput, Segmented, TextInput } from '../ui/Field.jsx';
import { Icon } from '../ui/Icon.jsx';
import { fmt } from '../../lib/format.js';

  const { useState, useEffect } = React;

  const g1 = (v) => (v == null || isNaN(v) ? '—' : new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v));

  /* ── KbjuTable — КБЖУ: строки порция/100 г × Ккал/Б/Ж/У ── */
  function KbjuTable({ per_portion, per_100g, className }) {
    const r = (label, d) => (
      <tr>
        <th scope="row" className="kbju__rowhead">{label}</th>
        <td className="num">{d ? (d.kcal == null ? '—' : Math.round(d.kcal)) : '—'}</td>
        <td className="num">{d ? g1(d.protein) : '—'}</td>
        <td className="num">{d ? g1(d.fat) : '—'}</td>
        <td className="num">{d ? g1(d.carbs) : '—'}</td>
      </tr>
    );
    return (
      <table className={'kbju' + (className ? ' ' + className : '')}>
        <thead>
          <tr>
            <th scope="col" />
            <th scope="col" className="num">Ккал</th>
            <th scope="col" className="num">Белки</th>
            <th scope="col" className="num">Жиры</th>
            <th scope="col" className="num">Угл.</th>
          </tr>
        </thead>
        <tbody>{r('На порцию', per_portion)}{r('На 100 г', per_100g)}</tbody>
      </table>
    );
  }

  /* ── CostSummaryBlock — сводка себестоимости (нередактируема) ──
     Полуфабрикат (kind='semiproduct') не продаётся гостю → ни цены, ни food-cost
     у него нет, показываем только себестоимость (см. редактор/canvas-черновик). */
  function CostSummaryBlock({ cost_per_portion_rub, cost_per_100g_rub, output_g, selling_price_rub, cost_status, fc_pct, kind, className }) {
    const showFoodCost = kind !== 'semiproduct' && selling_price_rub != null;
    return (
      <div className={'cost-summary derived-zone' + (className ? ' ' + className : '')}>
        <div className="cost-summary__row">
          <span className="cost-summary__key">Себестоимость порции</span>
          <span className="cost-summary__val num">{fmt.money(cost_per_portion_rub)}</span>
        </div>
        <div className="cost-summary__row">
          <span className="cost-summary__key">Себестоимость на 100 г</span>
          <span className="cost-summary__val num">{fmt.money(cost_per_100g_rub)}</span>
        </div>
        <div className="cost-summary__row">
          <span className="cost-summary__key">Полнота себестоимости</span>
          <CostStatusBadge status={cost_status} size="sm" />
        </div>
        {showFoodCost && (
          <div className="cost-summary__row">
            <span className="cost-summary__key">Food cost</span>
            <FoodCostBadge value={fc_pct} size="sm" />
          </div>
        )}
      </div>
    );
  }

  /* ── TTKMetricsBlock — read-only метрики (КБЖУ + выход + себест.) ── */
  function TTKMetricsBlock({ kbju_per_portion, kbju_per_100g, output_g, cost_per_portion_rub, cost_per_100g_rub, cost_status, selling_price_rub, fc_pct, className }) {
    return (
      <div className={'ttk-metrics' + (className ? ' ' + className : '')}>
        <div className="ttk-metrics__kbju">
          <KbjuTable per_portion={kbju_per_portion} per_100g={kbju_per_100g} />
        </div>
        <CostSummaryBlock cost_per_portion_rub={cost_per_portion_rub} cost_per_100g_rub={cost_per_100g_rub}
          output_g={output_g} selling_price_rub={selling_price_rub} cost_status={cost_status} fc_pct={fc_pct} />
      </div>
    );
  }

  /* ── TTKSellingPriceBlock — цена продажи (edit) + derived FC% ── */
  function TTKSellingPriceBlock({ selling_price_rub, cost_per_portion_rub, fc_pct, onChange, saving, className }) {
    const tone = fcTone(fc_pct);
    return (
      <div className={'selling-price' + (className ? ' ' + className : '')}>
        <div className="selling-price__field">
          <label className="field__label">Цена продажи</label>
          <NumberInput value={selling_price_rub ?? ''} unit="₽" step={10} min={0}
            onChange={(v) => onChange && onChange(v === '' ? null : Number(v))} />
        </div>
        <div className="selling-price__fc derived-zone">
          <span className="t-caption text-dim">Food cost</span>
          {selling_price_rub == null
            ? <span className="text-mute">Цена не задана</span>
            : <span className={'selling-price__fc-val num fc-tone-' + tone}>{fc_pct == null ? '—' : fmt.percent(fc_pct)}</span>}
          {saving && <Icon name="spinner" size={14} className="selling-price__spin" />}
        </div>
      </div>
    );
  }

  /* ── TTKUsedInBlock — где используется ТТК ── */
  function TTKUsedInBlock({ parents = [], menus = [], onOpenTtk, onOpenMenu, className }) {
    if (!parents.length && !menus.length) {
      return <div className={'used-in used-in--empty' + (className ? ' ' + className : '')}><span className="text-mute">Эта ТТК пока нигде не используется.</span></div>;
    }
    return (
      <div className={'used-in' + (className ? ' ' + className : '')}>
        {parents.length > 0 && (
          <section className="used-in__sec">
            <h4 className="used-in__title t-caption">Входит в ТТК ({parents.length})</h4>
            <ul className="used-in__list">
              {parents.map((p) => (
                <li key={p.ttk_id}>
                  <button type="button" className="used-in__link" onClick={() => onOpenTtk && onOpenTtk(p.ttk_id)}>
                    <Icon name="file-text" size={15} /><span className="grow truncate">{p.ttk_title}</span>
                    <span className="used-in__kind t-caption">{p.ttk_kind_label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        {menus.length > 0 && (
          <section className="used-in__sec">
            <h4 className="used-in__title t-caption">Входит в меню ({menus.length})</h4>
            <ul className="used-in__list">
              {/* бэк used-in отдаёт {menu_id, name, status} — поле именно `name` */}
              {menus.map((m) => (
                <li key={m.menu_id}>
                  {onOpenMenu ? (
                    <button type="button" className="used-in__link" onClick={() => onOpenMenu(m.menu_id)}>
                      <Icon name="layers" size={15} /><span className="grow truncate">{m.name || m.menu_name}</span>
                    </button>
                  ) : (
                    <span className="used-in__link used-in__link--static">
                      <Icon name="layers" size={15} /><span className="grow truncate">{m.name || m.menu_name}</span>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    );
  }

  /* ── IngredientName — имя строки: name_raw (серый) + привязка (чёрный) + badge ──
     Для НЕпривязанной строки с onNameChange — редактируемое поле (ручной ввод
     названия: «добавить вручную → AI заведёт карточку»). */
  function IngredientName({ row, onOpenTarget, onNameChange }) {
    const bound = row.target_kind === 'card' || row.target_kind === 'ttk';
    if (!bound && typeof onNameChange === 'function') {
      return (
        <div className="ing-name ing-name--edit">
          <TextInput value={row.name_raw || ''} placeholder="Название ингредиента" className="ing-name__input"
            onChange={(e) => onNameChange(e.target.value)} />
        </div>
      );
    }
    const clickable = bound && typeof onOpenTarget === 'function';
    const label = row.target_label || row.name_raw || '—';
    const inner = (
      <>
        {row.target_kind === 'ttk' && <Icon name="box" size={13} className="ing-name__semi" />}
        <span className="truncate">{label}</span>
      </>
    );
    return (
      <div className="ing-name">
        {clickable
          ? <button type="button" className="ing-name__target" onClick={() => onOpenTarget(row)}>{inner}</button>
          : <span className="ing-name__target ing-name__target--static">{inner}</span>}
      </div>
    );
  }

  /* ── RecipeBindingSummary — «Привязано N/M» ── */
  function RecipeBindingSummary({ bound, total, className }) {
    if (!total) return null;
    const partial = bound < total;
    return (
      <div className={'binding-summary' + (partial ? ' binding-summary--partial' : '') + (className ? ' ' + className : '')}>
        <Icon name={partial ? 'unlink' : 'link'} size={14} />
        <span>Привязано <span className="num">{bound}/{total}</span> ингредиентов</span>
      </div>
    );
  }

  /* ── YieldHint — подсказка к полю «Выход» ── */
  function YieldHint() {
    return (
      <span className="yield-hint" data-tip="Масса одной готовой порции блюда после тепловой обработки">
        <Icon name="info" size={14} />
      </span>
    );
  }

  /* ── PortionsSelector / ScaleNormBlock — масштаб нормы ── */
  const SCALE_OPTS = [
    { value: '0.5', label: '×0.5' }, { value: '1', label: '×1' }, { value: '2', label: '×2' },
    { value: '4', label: '×4' }, { value: '6', label: '×6' },
  ];
  function PortionsSelector({ value = '1', onChange, className }) {
    return (
      <div className={'portions-sel' + (className ? ' ' + className : '')}>
        <span className="t-caption text-dim portions-sel__label"><Icon name="scale" size={14} />Масштаб нормы</span>
        <Segmented options={SCALE_OPTS} value={String(value)} onChange={onChange} size="sm" compact ariaLabel="Масштаб нормы" />
      </div>
    );
  }

  /* ── TTKSnapshotView — read-only снимок версии (S4 использует полноценно) ── */
  function TTKSnapshotView({ snapshot, className }) {
    if (!snapshot) return null;
    const rows = snapshot.ingredient_rows || [];
    return (
      <div className={'snapshot' + (className ? ' ' + className : '')}>
        <div className="snapshot__head">
          <h4 className="t-h4">{snapshot.title}</h4>
          <span className="t-caption text-dim">Выход: <span className="num">{fmt.grams(snapshot.output_g)}</span></span>
        </div>
        {rows.length > 0 && (
          <table className="snapshot__rows">
            <thead><tr><th>Ингредиент</th><th className="num">Брутто</th><th className="num">Нетто</th><th className="num">% потерь</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={r.deleted ? 'is-deleted' : ''}>
                  <td>{r.target_label || r.name_raw}{r.deleted && <span className="badge badge--bad badge--sm">удалено</span>}</td>
                  <td className="num">{g1(r.amount_brutto_g)}</td>
                  <td className="num">{g1(r.amount_netto_g)}</td>
                  <td className="num derived-cell">{r.cold_loss_pct == null ? '—' : fmt.percent(r.cold_loss_pct, { digits: 1 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

export { KbjuTable };
export { CostSummaryBlock };
export { TTKMetricsBlock };
export { TTKSellingPriceBlock };
export { TTKUsedInBlock };
export { IngredientName };
export { RecipeBindingSummary };
export { YieldHint };
export { PortionsSelector };
export { TTKSnapshotView };
